import React, { useEffect, useState } from 'react';
import './register.scss';
import { useGetPreRegisterInfo, usePreRegistration } from '../Hooks/useRegister';
import { BizSelect } from '../../../../../Js/React/UI/BizSelect/BizSelect';
import { getPathWSiteRoot } from '../../../../../Js/React/Services/common';
import BizPhrase from '../../../../../FrontEnd/Components/Phrase';
import { getTranslation } from '../../../../../Js/React/Services/legacy';
import { getPortalSetting } from '../../../../../FrontEndReact/src/Core/Services/portal';
import { useBizTranslation } from '../../../../../FrontEnd/Common/Hooks/useBizTranslation';

type Step = 'EMAIL' | 'COMPANY' | 'VERIFY_COMPANY' | 'SUBMITTED';

const requireVerifyCompany = false;

const Register = () => {
	const [email, setEmail] = useState('');
	const [selectedCompanyId, setSelectedCompanyId] = useState('-1');
	const [selectedCustomerNumber, setSelectedCustomerNumber] = useState('');
	const [step, setStep] = useState<Step>('EMAIL');
	const [localError, setLocalError] = useState<string>();
	const { data: preRegData, btnStatus: preRegBtnStatus, getPreRegistrationInfo } = useGetPreRegisterInfo();
	const { result, btnStatus, preRegistration, clear: clearResult } = usePreRegistration();
	const selectTranslation = useBizTranslation('BIGCUSTOM.REGISTERCUSTOM.SELECTCOMPANY');

	useEffect(() => {
		if ((preRegData?.Companies?.length ?? 0) > 0) {
			setStep('COMPANY');
		}
		setLocalError(undefined);
	}, [preRegData?.Companies]);

	useEffect(() => {
		if (result !== null && result.Success) {
			setStep('SUBMITTED');
		}
		setLocalError(undefined);
	}, [result]);

	const emailChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.currentTarget.value);
		if (step !== 'EMAIL' && step !== 'SUBMITTED') {
			setStep('EMAIL');
			clearResult();
			setLocalError(undefined);
		}
	};

	const getCompaniesToDisplay = () => {
		if (preRegData?.Companies === undefined || preRegData?.Companies === null) {
			return null;
		}
		const companies = [{ Id: '-1', Name: selectTranslation ?? '-- Välj --' }, ...preRegData.Companies];
		return companies;
	};

	const nextHandler = () => {
		if (step === 'EMAIL') {
			getPreRegistrationInfo(email);
		} else if (step === 'COMPANY') {
			if (requireVerifyCompany) {
				setStep('VERIFY_COMPANY');
				setSelectedCustomerNumber('');
			} else {
				preRegistration(email, Number(selectedCompanyId));
			}
		} else if (step === 'VERIFY_COMPANY') {
			const selectedComp = preRegData?.Companies.find((c) => c.Id === selectedCompanyId);
			if (selectedComp?.BIGCustomerCode.toLowerCase() === selectedCustomerNumber.toLowerCase()) {
				preRegistration(email, Number(selectedCompanyId));
			} else {
				getTranslation('BIGCUSTOM.REGISTERCUSTOM.CUSTOMERNUMBERERROR').then((res) => {
					setLocalError(res ?? 'Kundnr stämmer inte');
				});
			}
		}
	};

	const getLogo = () => {
		const logoUrl = getPortalSetting('logo');
		return getPathWSiteRoot(logoUrl);
	};

	const getRedirectUrl = () => {
		if (typeof result?.Object === 'object' && result?.Object !== null) {
			const obj = result.Object as { RedirectUrl?: unknown };
			if (typeof obj.RedirectUrl === 'string') {
				return obj.RedirectUrl;
			}
		}
	};

	const getError = (): { error: string; redirectUrl?: string } | undefined => {
		if (result?.Success === true) {
			return undefined;
		} else if (localError !== undefined) {
			return { error: localError };
		} else if (preRegData?.Message !== undefined && preRegData.Success === false) {
			return { error: preRegData.Message };
		} else if (result?.Message !== undefined && result.Success === false) {
			return { error: result.Message, redirectUrl: getRedirectUrl() };
		}
	};

	const logo = getLogo();
	const companies = getCompaniesToDisplay();
	const error = getError();

	const loading = btnStatus === 'SAVING' || preRegBtnStatus === 'SAVING';

	return (
		<div className='cleanbase-form-wrapper big-register'>
			<div>
				<div className='logo'>
					<img src={logo} alt='logo' />
				</div>
				<div className='header-wrapper'>
					<h1>
						<BizPhrase phraseKey='BIGCUSTOM.REGISTERCUSTOM.HEADER'></BizPhrase>
					</h1>
				</div>
				<div className={'input-wrapper'}>
					<span className='input-label'>
						<BizPhrase phraseKey='BIGCUSTOM.REGISTERCUSTOM.EMAIL'></BizPhrase>
					</span>
					<input value={email} onChange={emailChangeHandler} />
				</div>
				{(step === 'COMPANY' || step === 'VERIFY_COMPANY' || step === 'SUBMITTED') &&
					preRegData?.Success &&
					companies !== null && (
						<div className={'input-wrapper'}>
							<span className='input-label'>
								<BizPhrase phraseKey='BIGCUSTOM.REGISTERCUSTOM.COMPANY'></BizPhrase>
							</span>
							<BizSelect
								options={companies}
								selectedId={selectedCompanyId}
								onChange={(option) => setSelectedCompanyId(option?.Id ?? '-1')}
							></BizSelect>
						</div>
					)}
				{requireVerifyCompany && (step === 'VERIFY_COMPANY' || step === 'SUBMITTED') && (
					<div className='input-wrapper'>
						<span className='input-label'>
							<BizPhrase phraseKey='BIGCUSTOM.REGISTERCUSTOM.VERIFYCOMPANY'></BizPhrase>
						</span>
						<input
							value={selectedCustomerNumber}
							onChange={(e) => setSelectedCustomerNumber(e.currentTarget.value)}
						/>
					</div>
				)}
				{step !== 'SUBMITTED' && (
					<div className='register-btn-wrapper'>
						<span className='biz-btn biz-primary' onClick={nextHandler}>
							{loading ? (
								<i className='fas fa-circle-notch fa-spin'></i>
							) : (
								<span>
									<BizPhrase phraseKey='BIGCUSTOM.REGISTERCUSTOM.NEXT'></BizPhrase>
								</span>
							)}
						</span>
					</div>
				)}
				{error?.error !== undefined && (
					<div className='result'>
						{error.redirectUrl === undefined && <span className='error'>{error.error}</span>}
						{error.redirectUrl !== undefined && (
							<span className='error'>
								<a href={error.redirectUrl}>{error.error}</a>
							</span>
						)}
					</div>
				)}
				{result?.Success === true && (
					<div className='result'>
						<span className='success'>{result.Message}</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default Register;
